/**
 * Pure mapping / derivation helpers for the MatrimonyProfileDetails page.
 * Kept separate from the component to keep the page file lean and testable.
 */
import type { MarriageProfileDto } from "src/types/Profile";
import type { MarriageProfileSection } from "src/types/marriageProfileSectionEdit.types";
import type { MatrimonyProfileDetailsData } from "src/types/matrimonyProfileDetails";
import { computeAge, toCurrency, toHeightLabel } from "src/utils/formatUtils";
import {
  DRINKING_HABIT_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  NAKSHATRA_OPTIONS,
  RASI_OPTIONS,
  SMOKING_HABIT_OPTIONS,
  getOptionLabel,
} from "src/utils/matrimonyOptions";

// ── Types ────────────────────────────────────────────────────────────

export type PhotoState = {
  rawFileName: string;
  objectUrl: string;
};

// ── DTO → Page-data mapper ───────────────────────────────────────────

export const mapDtoToPageData = (dto: MarriageProfileDto): MatrimonyProfileDetailsData => {
  // NOTE: do not set photoUrl here with /images/* directly if endpoint requires auth.
  // A blob/object URL will be injected after fetch.
  const photoUrl = "";

  return {
    summary: {
      id: String(dto.id ?? ""),
      name: String(dto.fullName || ""),
      managedBy: "Self",
      photoUrl,
      photoAlt: "Profile photo",
      profilePhotoFileName: dto.profilePhoto || "",
      email: dto.email ? String(dto.email) : "",
      mobileNo: dto.mobileNo ? String(dto.mobileNo) : "",
      stats: {
        age: computeAge(dto.dateOfBirth),
        height: toHeightLabel(dto.heightInCm),
        location: String(dto.nativePlace || ""),
        jobRole: String(dto.designation || ""),
        community: String(dto.casteDetails || ""),
      },
    },
    personal: {
      dob: String(dto.dateOfBirth || ""),
      maritalStatus: getOptionLabel(MARITAL_STATUS_OPTIONS, dto.maritalStatus),
      gender: String(dto.gender || ""),
    },
    professional: {
      education: String(dto.highestEducation || ""),
      college: String(dto.collegeOrUniversity || ""),
      annualIncome: toCurrency(dto.annualIncome),
      company: String(dto.employerName || ""),
    },
    family: {
      fatherName: String(dto.fatherName || ""),
      fatherStatus: String(dto.fatherOccupation || ""),
      motherName: String(dto.motherName || ""),
      motherStatus: String(dto.motherOccupation || ""),
      familyType: String(dto.familyType || ""),
    },
    astro: {
      rasi: getOptionLabel(RASI_OPTIONS, dto.rasi),
      nakshatra: getOptionLabel(NAKSHATRA_OPTIONS, dto.nakshatra),
      birthPlace: String(dto.nativePlace || ""),
    },
    lifestyle: {
      diet: String(dto.dietaryHabits || ""),
      drinking: getOptionLabel(DRINKING_HABIT_OPTIONS, dto.drinkingHabit),
      smoking: getOptionLabel(SMOKING_HABIT_OPTIONS, dto.smokingHabit),
      ownsHouse: dto.ownsHouse === true ? "Yes" : dto.ownsHouse === false ? "No" : "",
    },
  };
};

// ── Section edit initial-values builder ──────────────────────────────

export const buildSectionInitialValues = (
  section: MarriageProfileSection,
  pageData: MatrimonyProfileDetailsData,
  dto: MarriageProfileDto,
): Record<string, unknown> => {
  switch (section) {
    case "SUMMARY":
      return {
        fullName: pageData.summary.name || "",
        about: dto.about || "",
      };
    case "PERSONAL":
      return {
        dob: pageData.personal.dob || "",
        maritalStatus: dto.maritalStatus || "",
        gender: dto.gender || "",
        casteDetails: dto.casteDetails || "",
        religion: dto.religion || "",
      };
    case "PROFESSIONAL":
      return {
        highestEducation: pageData.professional.education || "",
        collegeOrUniversity: pageData.professional.college || "",
        employmentSector: dto.employmentSector || "",
        designation: dto.designation || "",
        employerName: pageData.professional.company || "",
        annualIncome: dto.annualIncome != null ? String(dto.annualIncome) : "",
        socialMediaLinksList: dto.socialMediaLinksList || null,
      };
    case "FAMILY":
      return {
        fatherName: dto.fatherName || "",
        motherName: dto.motherName || "",
        fatherOccupation: dto.fatherOccupation || "",
        motherOccupation: dto.motherOccupation || "",
        familyType: dto.familyType || "",
        familyStatus: dto.familyStatus || "",
        nativePlace: dto.nativePlace || "",
        totalFamilyMembers: dto.totalFamilyMembers != null ? String(dto.totalFamilyMembers) : "",
      };
    case "LIFESTYLE":
      return {
        heightCm: dto.heightInCm != null ? String(dto.heightInCm) : "",
        bodyType: dto.bodyType || "",
        skinTone: dto.skinTone || "",
        dietaryHabits: dto.dietaryHabits || "",
        hobbies: dto.hobbiesInterests || "",
        smokingHabit: dto.smokingHabit || "",
        drinkingHabit: dto.drinkingHabit || "",
        ownsHouse: dto.ownsHouse === true ? "true" : dto.ownsHouse === false ? "false" : "",
      };
    case "ASTRO":
    default:
      return {
        nakshatra: dto.nakshatra || "",
        rasi: dto.rasi || "",
        gothram: dto.gothram || "",
        dosham: dto.dosham || "",
        timeOfBirth: dto.timeOfBirth || "",
      };
  }
};

// ── Social-links normaliser ──────────────────────────────────────────

/**
 * Returns `true` when a social-media link value is the masked placeholder
 * the backend sends for unconnected profiles (e.g. `"*****@social_media.***"`).
 */
const isMaskedLink = (url: string): boolean =>
  /^\*{3,}/.test(url) || url.includes("@social_media.");

export const normaliseSocialLinks = (
  raw: MarriageProfileDto["socialMediaLinksList"],
): Array<{ platform: string; url: string }> => {
  const list = Array.isArray(raw) ? raw : [];
  return list
    .map((x: any) => ({
      platform: String(x?.socialMediaPlatforms ?? x?.platform ?? ""),
      url: String(x?.link ?? x?.url ?? ""),
    }))
    .filter((x) => x.platform && x.url && !isMaskedLink(x.url));
};
